import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { Scrollbars } from 'react-custom-scrollbars';
import axios from 'axios';
import {
  API_URL,
  defaultMeasureCodeIdsToDisplay,
  IMAGE_URL,
  LOCATION_IMAGE_URL,
} from 'config.jsx';
import BasicInformation from './BasicInformation/BasicInformation';
import ListBox, { ListBoxWithDropDown } from './ListBox/ListBox';
import PictureViewerBox from './PictureViewer/PictureViewerBox';

const styles = theme => ({
  root: {
    margin: '30px 0 30px 0',
    flexGrow: 1,
    justifyContent: 'center',
  },
  infoBox: {
    padding: '12px',
    height: '300px',
    minHeight: '300px',
    [theme.breakpoints.down('xs')]: {
      height: '277px',
      minHeight: '277px',
    },
    display: 'inline-block',
  },
});

export class Overview extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      latestSpecificMeasures: [],
      latestBasicMeasures: [],
      latestEvents: [],
      basicInfo: [],
      loadingSpecificMeasure: false,
      loadingBasicMeasures: false,
    };

    this.getLastBasicMeasuresAndSetThemToState = this
      .getLastBasicMeasuresAndSetThemToState.bind(this);
    this.getMeasuresAndSetThemToState = this.getMeasuresAndSetThemToState.bind(this);
  }

  componentDidMount() {
    const { elephant: { events }, elephant } = this.props;
    this.getLastBasicMeasuresAndSetThemToState();
    this.setBasicInfoToState(events, elephant);
    this.mapEventsToListItemAndSetToState(events);
    this.getMeasuresAndSetThemToState(elephant.measureCodes[0] && elephant.measureCodes[0].pk);
  }

  getMeasuresAndSetThemToState(selectedMeasureCodeId) {
    if (selectedMeasureCodeId == null) return;
    this.setState({ loadingSpecificMeasure: true });
    const { elephant } = this.props;
    const unitOfMeasure = elephant.measureCodes
      .find(measureCode => measureCode.pk === selectedMeasureCodeId).unit;
    axios.get(`${API_URL}elephants/${elephant.pk}/measurecodes/${selectedMeasureCodeId}/measures/`)
      .then((response) => {
        const measures = response.data;
        measures.sort((a, b) => new Date(b.date) - new Date(a.date))
          .forEach((measure) => { measure.value += ` ${unitOfMeasure}`; });
        this.setState({ latestSpecificMeasures: measures, loadingSpecificMeasure: false });
      })
      .catch(() => this.setState({ latestSpecificMeasures: [], loadingSpecificMeasure: false }));
  }

  getMeasureCodeIdsToDisplayInBasicInformation(idsGiven, idsAvailable) {
    if (!idsAvailable || idsAvailable.length === 0) return [];
    const measureCodeIdsToDisplay = idsGiven || defaultMeasureCodeIdsToDisplay;
    const availableMeasureCodes = idsAvailable.length > 0 && idsAvailable.map(code => code.pk);
    const validMeasureCodeIdsToDisplay = measureCodeIdsToDisplay
      .filter(c => availableMeasureCodes.indexOf(c) !== -1);

    return validMeasureCodeIdsToDisplay;
  }

  getLastBasicMeasuresAndSetThemToState(customMeasureCodeIdsToDisplay) {
    this.setState({ loadingBasicMeasures: true });
    const { elephant } = this.props;
    const measureCodeIdsToDisplay = this.getMeasureCodeIdsToDisplayInBasicInformation(
      customMeasureCodeIdsToDisplay, elephant.measureCodes,
    );
    const requestUrls = measureCodeIdsToDisplay.map(
      id => axios.get(`${API_URL}elephants/${elephant.pk}/measurecodes/${id}/measures/`),
    );
    Promise.all(requestUrls.map(p => p.catch(e => e)))
      .then((responses) => {
        this.setState({
          latestBasicMeasures: responses.map(r => (
            r.data && r.data.length > 0 ? r.data.pop() : null)).filter(r => r != null),
          loadingBasicMeasures: false,
        });
      });
  }

  getLastKnownLocation(events) {
    const chronologicalEvents = events.slice().reverse();
    const latestEventWithLocation = chronologicalEvents.find(event => event.location != null);
    if (latestEventWithLocation && latestEventWithLocation.location) {
      return latestEventWithLocation.location;
    }
    const { elephant } = this.props;
    return elephant.camp;
  }

  getAge(elephant) {
    if (elephant.alive_state === 'dead') return elephant.lifespan ? `died at ${parseInt(elephant.lifespan, 10)}` : 'Unknown';
    return elephant.lifespan ? `${parseInt(elephant.lifespan, 10)} years` : 'Unknown';
  }

  setBasicInfoToState(events, elephant) {
    const lastKnownLocation = this.getLastKnownLocation(events);
    const age = this.getAge(elephant);
    const basicInfo = {
      'last known location': lastKnownLocation ? lastKnownLocation.name : 'unknown',
      'alive or dead': elephant.alive_state,
      sex: elephant.sex === 'UKN' ? 'unknown' : elephant.sex || 'unknown',
      'captive or wild': elephant.cw ? elephant.cw : 'unknown',
      age: age || 'unknown',
    };
    this.setState({ basicInfo });
  }

  mapMeasuresToBasicInfoItem(latestBasicMeasures, availableMeasureCodes) {
    const basicMeasureInfo = {};
    if (latestBasicMeasures && availableMeasureCodes) {
      availableMeasureCodes.forEach((code) => {
        latestBasicMeasures.forEach((measure) => {
          if (code.pk === measure.code) {
            basicMeasureInfo[`${code.label.name} (${measure.date})`] = `${measure.value} ${code.unit}`;
          }
        });
      });
    }
    return basicMeasureInfo;
  }

  mapEventsToListItemAndSetToState(events) {
    const newEvents = events.slice().reverse().slice(0, 5).map((event) => {
      let eventValue = event.title;
      if (event.type === 'reproduction') {
        eventValue = `${event.title} ${event.details}`;
      }
      const eventDate = event.date ? event.date.format('YYYY-MM-DD') : 'Unknown date';
      return Object.assign({}, event, { value: eventValue }, { date: eventDate });
    });
    this.setState({ latestEvents: newEvents });
  }

  render() {
    const {
      classes, elephant: {
        measureCodes: availableMeasureCodes, images: imageUrls, events,
      },
    } = this.props;
    const {
      loadingSpecificMeasure,
      loadingBasicMeasures,
      latestSpecificMeasures,
      latestBasicMeasures,
      latestEvents,
      basicInfo,
    } = this.state;

    const basicMeasureInfo = this.mapMeasuresToBasicInfoItem(
      latestBasicMeasures, availableMeasureCodes,
    );

    const lastKnownLocation = this.getLastKnownLocation(events);
    const lastLocationCode = lastKnownLocation ? `${lastKnownLocation.code}.png` : 'UNKNOWN.png';
    const components = [
      <BasicInformation
        title="General information"
        data={basicInfo}
      />,
    ];
    if (imageUrls.length > 0) {
      components.push(
        <PictureViewerBox
          displayPictureAmount
          title="Pictures"
          imageUrls={imageUrls.map(url => `${IMAGE_URL}${url}`)}
        />,
      );
    }

    components.push(
      <PictureViewerBox
        title="Last known location"
        imageUrls={[`${LOCATION_IMAGE_URL}${lastLocationCode}`]}
      />,
      <BasicInformation
        title="Morphology"
        data={basicMeasureInfo}
        loading={loadingBasicMeasures}
        updateValues={this.getLastBasicMeasuresAndSetThemToState}
        availableData={availableMeasureCodes}
      />,
      <ListBox
        title="Latest events"
        data={latestEvents}
      />,
      <ListBoxWithDropDown
        title="Latest measures"
        data={latestSpecificMeasures.slice(0, 5)}
        loading={loadingSpecificMeasure}
        updateValues={this.getMeasuresAndSetThemToState}
        availableData={availableMeasureCodes}
      />,
    );

    return (
      <Scrollbars>
        <Grid container className={classes.root}>
          {components.map(component => (
            <Grid item key={component.props.title}>
              <div className={classes.infoBox}>
                {component}
              </div>
            </Grid>
          ))}
        </Grid>
      </Scrollbars>
    );
  }
}

Overview.propTypes = {
  elephant: PropTypes.shape({
    pk: PropTypes.number.isRequired,
    num: PropTypes.string,
    name: PropTypes.string,
    calf_num: PropTypes.string,
    sex: PropTypes.string.isRequired,
    birth: PropTypes.string,
    age_capture: PropTypes.number,
    cw: PropTypes.string.isRequired,
    research: PropTypes.string,
    camp: PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      level: PropTypes.string,
    }),
    events: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      details: PropTypes.string,
      date: PropTypes.shape(),
    })).isRequired,
    measureCodes: PropTypes.arrayOf(PropTypes.shape({
      pk: PropTypes.number.isRequired,
      class_field: PropTypes.string,
      type: PropTypes.string.isRequired,
      unit: PropTypes.string,
      descript: PropTypes.string,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
};

export default withStyles(styles)(Overview);



// WEBPACK FOOTER //
// ./src/components/overview/Overview.jsx